import { Component, inject, Input, OnInit } from '@angular/core';
import { DatePickerComponent } from "@shared/controls/date-picker/date-picker.component";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import { CompaniesApiService } from "@shared/services/companies-api.service";
import { DatepickerHelper } from "@shared/validators/date-picker-form-validators";
import { EntityChangeData } from "@models/documents/entity-change-data";
import { CompanyForm361 } from "../../../models/company";
import { Document } from "@models/documents/document";
import { DocumentationType } from "@models/documents/document-enums";
import { ColDef } from "ag-grid-community";
import {
  AgEntityNameAcnAbnComponent
} from "@shared/components/grid/ag-cells/common/ag-entity-name-acn-abn/ag-entity-name-acn-abn.component";
import { RadioComponent } from "@shared/controls/radio/radio.component";
import { Company } from "@models/companies/company";
import { combineLatest, forkJoin, Observable } from "rxjs";
import { ChangeAuthorisation } from "@models/documents/change-authorisation";
import { UserBrief } from "@models/organisations/ui-profile";
import {
  AgSelectChangeNameComponent
} from "@shared/components/grid/ag-cells/bulk-changes/ag-select-change-name/ag-select-change-name.component";
import { UserRecord } from '@models/organisations/user-record';
import { UsersApiService } from '@shared/services/users-api.service';
import {
  BaseBulkStepperFormComponent,
  StepperFormComponent,
  StepperFormDescriptionComponent
} from "../../stepper-form";
import { IBulkAppliedChange } from "../base-bulk-stepper-form/base-bulk-stepper-form.component";
import { CompanyRecord } from "@models/companies/company-record";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";

enum BulkCeaseRegisteredAgentStepsEnum {
  FormDescription = 0,
  RegisteredAgentCeasing = 1,
  Summary = 2,
}

@Component({
  selector: 'app-bulk-company-form-361',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    StepperFormComponent,
    StepperFormDescriptionComponent,
    DatePickerComponent,
    RadioComponent,
    ListGridClientSideComponent
  ],
  providers: [],
  templateUrl: 'bulk-company-form-361.component.html',
  styleUrls: ['../../stepper-form/base-stepper-component/base-stepper-form.component.scss']
})
export class BulkCompanyForm361Component extends BaseBulkStepperFormComponent<BulkCeaseRegisteredAgentStepsEnum, CompanyForm361> implements OnInit {
  usersService = inject(UsersApiService);

  @Input() companyRecords: CompanyRecord[] = [];

  override readonly StepsEnum = BulkCeaseRegisteredAgentStepsEnum;

  minDate!: NgbDateStruct;
  maxDate!: NgbDateStruct;
  currentProfileUser: UserBrief | undefined;
  users: UserRecord[] = [];

  form = new FormGroup({
    changeDate: new FormControl<Date | null>(DatepickerHelper.getToday(), [Validators.required]),
    documentationType: new FormControl<DocumentationType>(DocumentationType.FormOnly, [Validators.required]),
  });

  override stepperForm = new FormGroup({
    [BulkCeaseRegisteredAgentStepsEnum.FormDescription]: new FormGroup({}),
    [BulkCeaseRegisteredAgentStepsEnum.RegisteredAgentCeasing]: this.form,
    [BulkCeaseRegisteredAgentStepsEnum.Summary]: this.form,
  });

  constructor() {
    super();
    this.setupSteps(BulkCeaseRegisteredAgentStepsEnum);
  }

  ngOnInit(): void {
    this.minDate = DatepickerHelper.getDefaultMinDateStruct();
    this.maxDate = DatepickerHelper.getTodayStruct();

    this.setupChange();
    this.loadEntities();
  }

  onStepChange(stepIndex: BulkCeaseRegisteredAgentStepsEnum): void {
    if (stepIndex === BulkCeaseRegisteredAgentStepsEnum.Summary) {
      this.fillRowsErrors();
    }
  }

  loadEntities(): void {
    this.isLoading.set(true);
    combineLatest(
      this.usersService.getAssignableUsersCached(),
      this.loadCompanies()
    )
      .subscribe({
        next: ([users, fullCompanies]) => {
          this.currentProfileUser = this.organisationService.getCurrentUser();
          this.users = users;
          this.companies = fullCompanies.filter(Boolean) as Company[];
          this.fillRows(this.companies);
          this.isLoading.set(false);
        }
      });
  }

  loadCompanies(): Observable<(Company | null)[]> {
    const selectedEntitiesLoadRequests = this.companyRecords
      .map((companyRecord) => this.loadCompany(companyRecord.entityId));

    return forkJoin(selectedEntitiesLoadRequests);
  }

  override afterSubmit(changes: EntityChangeData[]): void {
    this.setupChange(changes[0] as CompanyForm361);
  }

  override setupChange(change: CompanyForm361 = this.formModel): void {
    this.form.controls.changeDate.setValue(change.changeDate);
  }

  override fillRows(entities: Company[]): void {
    super.fillRows(entities);
    const usersOptions = this.users?.map(user => ({ label: user.fullName, value: user.id }));

    this.rows = this.rows.map((row) => {
      row.options = usersOptions;
      row.selectedOption = usersOptions?.find((option) => option.value == this.signingManager)!;
      return row;
    });
  }

  override buildDocument(): Document[] | null {
    if (this.areAllRowsHaveErrors) return null;

    const changeDate = DatepickerHelper.buildDateString(this.form.controls.changeDate.value!);
    const change = new CompanyForm361({ changeDate });
    const documentationType = DocumentationType.FormOnly;

    try {
      return this.companies.map((company, index) => {
        const relatedRow = this.rows[index];
        const changeAuthorisation = this.getChangeAuthorisation(company, documentationType, relatedRow);

        return new Document({
          changes: [change],
          changeAuthorisation,
          entityId: company.entityId,
          type: 'c:361',
          documentId: this.companyChangeData?.documentId,
          documentationType
        });
      });
    } catch (error) {
      console.warn(error);
      this.toastr.error('Failed to create Document.', 'Error');
      return null;
    }
  }

  onChangeEntitySigningManager(data: { entityId: string, selectedId: string }): void {
    const relatedRow = this.rows.find((row) => row.entityId == data.entityId);
    const selectedOfficer = this.users.find((user) => user.id == data.selectedId);

    if (relatedRow && selectedOfficer) {
      // Selected used.id for ChangeAuthorisation filling
      relatedRow.selectedOption = { value: data.selectedId, label: '' };
    }
  }

  override updateDocumentationTypeOptions(): void {
    this.documentTypeOptions = this.documentTypeOptions.map((option) => ({
      ...option,
      disabled: option.value !== DocumentationType.FormOnly
    }));
    this.form.controls.documentationType.setValue(this.documentTypeOptions.find((option) => !option.disabled)?.value as DocumentationType ?? null);
  }

  override getChangeAuthorisation(entity: Company, selectedDocumentationType: DocumentationType, relatedRow: IBulkAppliedChange, changeDate?: Date): ChangeAuthorisation {
    const changeAuthorisation = super.getChangeAuthorisation(entity, selectedDocumentationType, relatedRow);
    changeAuthorisation.signingManager = relatedRow.selectedOption.value as string;

    return changeAuthorisation;
  }

  // ===== ===== ===== Handle "Change is applied for" section and it's grid ===== ===== =====

  override readonly baseColDefs: ColDef[] = [
    {
      headerName: 'Entity Name',
      field: 'name',
      sort: 'asc',
      flex: 0.6,
      cellRenderer: AgEntityNameAcnAbnComponent,
      menuTabs: []
    },
    {
      headerName: 'ASIC Contact',
      sortable: false,
      flex: 1,
      cellRenderer: AgSelectChangeNameComponent,
      cellRendererParams: {
        selectName: this.onChangeEntitySigningManager.bind(this),
      },
      menuTabs: []
    },
  ];

  override getRowError(row: IBulkAppliedChange, rowIndex: number): string {
    const company = this.companies[rowIndex];

    const relatedChangeAddressEntityEstablishmentDate = company.dateOfEstablishment
      ? new Date(company.dateOfEstablishment)
      : company.dateOfEstablishment;
    const changeDate = this.form.controls.changeDate.value;

    if (relatedChangeAddressEntityEstablishmentDate && changeDate && changeDate <= relatedChangeAddressEntityEstablishmentDate) {
      return 'Date of change cannot be earlier or same as the company registration date';
    }

    return super.getRowError(row, rowIndex);
  }
}
